export interface iEmsApiResponse {
    isOk: boolean;
    status: number;
    statusText: string;
    validationErrors?: iValidationError | undefined;
    serverError?: iError | undefined;
    /** The ProblemDetails body of a 409. Its `detail` says what the conflict is. */
    conflict?: iProblemDetails | undefined;
    errorLogID?: string | undefined;
}

export interface iProblemDetails {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
}

export interface iEmsApiResponseBody<TResponse> extends iEmsApiResponse {
    data?: TResponse | undefined;
}

export interface iValidationError_old {
    title: string;
    errors: {
        [key: string]: string[];
    };
}
export interface iValidationError {
    [key: string]: string[];
}

export interface iError {
    type: string;
    title: string;
    status: number;
    traceId: string;
}

/**
 * The absolute URL of an EMS API route.
 *
 * Split out of `CallEmsApi` so a caller that needs the raw `Response` — a
 * file download, which must not go through `response.json()` — builds
 * exactly the same URL.
 */
export function EmsApiUrl(url: string): string {
    let sURL = process.env.REACT_APP_API;
    if (!sURL || sURL.length === 0) {
        var str = window.location;
        let stringtoReplaces = str.origin.substring(
            str.protocol.length + 2,
            str.origin.indexOf(".", str.protocol.length + 2),
        );
        sURL = str.origin.replace(stringtoReplaces, "api");
    }
    if (!sURL.endsWith("/")) {
        sURL += "/";
    }

    return sURL + url;
}

export async function CallEmsApi<TResponse>(
    accessToken: string,
    method: "GET" | "POST" | "PUT" | "DELETE",
    url: string,
    body?: any,
): Promise<iEmsApiResponseBody<TResponse>> {
    const emsResponse: iEmsApiResponseBody<TResponse> = {
        isOk: false,
        status: 0,
        statusText: "",
        data: undefined,
        validationErrors: undefined,
        serverError: undefined,
    };

    const sURL = EmsApiUrl(url);

    const headers = new Headers();
    headers.append("Authorization", "bearer " + accessToken);

    if (body) {
        headers.append("Content-Type", "application/json");
    }

    var fetchOptions: any = {};
    fetchOptions.method = method;
    fetchOptions.headers = headers;
    if (body) {
        fetchOptions.body = JSON.stringify(body);
    }

    var response = await fetch(sURL, fetchOptions);

    emsResponse.isOk = response.ok;
    emsResponse.status = response.status;
    emsResponse.statusText = response.statusText;

    if (response.ok && response.status === 200) {
        emsResponse.data = await response.json();
    } else if (response.status === 400) {
        emsResponse.validationErrors = await response.json();
    } else if (response.status === 500) {
        emsResponse.serverError = await response.json();
    } else if (response.status === 409) {
        // A 409 with no JSON body still has to reach the caller as a 409.
        emsResponse.conflict = await response.json().catch(() => undefined);
    } else if (!response.ok) {
    }

    return emsResponse;
}

/**
 * The response shape for a failure that never reached HTTP.
 *
 * `CallEmsApi` maps HTTP statuses into its return value but does NOT wrap its
 * fetch, so a network-level failure rejects rather than returning. Callers
 * that must not propagate — anything rendering a page — catch and return this
 * instead. Status 0 is the marker: no HTTP exchange took place, so there is no
 * real status to report.
 *
 * Lives here rather than in either caller because it describes `CallEmsApi`'s
 * contract, not any one endpoint's. It was duplicated verbatim in CoreAPI.ts
 * and MailboxAPI.ts, whose copies each carried a comment saying so.
 */
export function networkFailure<TResponse>(): iEmsApiResponseBody<TResponse> {
    return {
        isOk: false,
        status: 0,
        statusText: "Network request failed",
        data: undefined,
    };
}

export interface GenericList {
    id: number;
    name: string;
}
export interface GetGenericListResponse {
    items: GenericList[];
}

export async function GetGenericLists(
    sURL: string,
    tenantToken: string,
): Promise<iEmsApiResponseBody<GetGenericListResponse>> {
    let results = await CallEmsApi<GetGenericListResponse>(tenantToken, "GET", sURL);
    if (results.isOk && results.data) {
        results.data.items = results.data.items.sort((n1, n2) => {
            if (n1.name > n2.name) {
                return 1;
            }

            if (n1.name < n2.name) {
                return -1;
            }

            return 0;
        });
    }
    return results;
}
